import Cookies, { CookieAttributes } from 'js-cookie';

import { getJwtExpiry } from '@/utils/jwt';

import { StorageStrategy } from './StorageStrategy';

const isLocalhost = () => {
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
};

// Fallback for values that aren't JWTs (i.e. don't carry their own `exp` claim). Matches the
// backend's refresh-token lifetime.
const DEFAULT_EXPIRY_IN_DAYS = 7;

export class CookieStorage implements StorageStrategy {
  private readonly baseOptions: CookieAttributes = {
    path: '/',
    domain: window.location.hostname,
    secure: !isLocalhost(), // we expect all environments except for localhost to run HTTPS
    sameSite: 'strict',
  };

  setItem(key: string, value: string): string | undefined {
    // A cookie should never outlive the token it stores - reading `exp` directly off the JWT
    // (rather than hardcoding a duration here) keeps the cookie lifetime correct even if the
    // backend's token TTL changes.
    const expires = getJwtExpiry(value) ?? DEFAULT_EXPIRY_IN_DAYS;
    Cookies.set(key, value, { ...this.baseOptions, expires });
    return Cookies.get(key);
  }

  getItem(key: string): string | undefined {
    return Cookies.get(key);
  }

  removeItem(key: string): void {
    return Cookies.remove(key, this.baseOptions);
  }

  getAll(): Record<string, string> {
    return Cookies.get();
  }
}

export const deleteAllCookies = () => {
  const cookieStorage = new CookieStorage();
  const allCookies = cookieStorage.getAll();

  Object.keys(allCookies).forEach((cookieName) => {
    cookieStorage.removeItem(cookieName);
  });
};
